import { useEffect, useState } from "react";

import { Loader, NotificationType } from "@transfr-inc/dashboard-components";
import {
  Button,
  SearchBox,
  useSearch,
} from "@transfr-inc/dashboard-components/forms";

import {
  RecordStatus,
  RecordStatusDropdown,
} from "@components/custom/dropdowns";
import { useNotificationContext } from "@contexts/notification-context";
import { trackLearnersAssignedToClassroom } from "@lib/event-trackers";
import { useApiRequest } from "@lib/hooks";
import { Role } from "@models/role";
import { useArchiveUserVisible } from "@queries";
import { getClassroomUsersMastery } from "@services/insights.utils";
import { useContainer } from "@src/contexts/container-context";

import { ClassroomUsersMasteryTable } from "@views/classrooms/details/components/classroom.users-mastery.table";
import { AddUserModal } from "@views/classrooms/details/modals/add-user.modal";
import { getUserLabelsByProduct } from "@views/classrooms/details/utils/labels";
import { ZeroStateMessage } from "@views/users/list/zero-state-message";

import "./classroom-users.scss";

const getSuccessMessage = (trainees, classroom) => {
  const { firstName, lastName } = trainees[0].data;
  const { name } = classroom;
  const traineesLength = trainees.length;

  return traineesLength > 1 ? (
    <>
      <strong>{traineesLength}</strong> users have been added to{" "}
      <strong>{name}</strong>
    </>
  ) : (
    <>
      <strong>{`${firstName} ${lastName}`}</strong> has been added to{" "}
      <strong>{name}</strong>
    </>
  );
};

export const ClassroomUsers = ({
  classroom,
  data,
  responsiveStyles,
  readOnly,
  onUsersUpdate,
  getUsers,
  openRosterModal,
  rosterModalOpened,
  productId,
  statusFilter = RecordStatus.Active,
  onStatusFilterChange,
  loading: loadingUsers,
}) => {
  const { classroomId, clientCode: orgCode, products } = classroom;
  const archiveUserVisible = useArchiveUserVisible();
  const showStatusFilter =
    archiveUserVisible && !classroom.isProfessionalLearning;
  const [users, setUsers] = useState();
  const [searchActive, setSearchActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [openAddUserModal, setOpenUserModal] = useState();
  const { addNotification } = useNotificationContext();
  const { classroomService, insightsService, simulationsService, trekService } =
    useContainer();
  const { addUser, singular, plural } = getUserLabelsByProduct(productId);

  const showSuccessNotification = (message) =>
    addNotification({
      type: NotificationType.success,
      icon: ["fa-solid", "circle-check"],
      message,
    });

  const { loading: loadingCareersExplored, response: careersExplored } =
    useApiRequest(() =>
      trekService.getUsersExportData({
        classroomId: classroom.classroomId,
      })
    );

  const {
    loading: loadingMasteryInfo,
    response: masteryInfo,
    sendRequest: getMasteryInfo,
  } = useApiRequest(() =>
    getClassroomUsersMastery(
      {
        insightsService,
        simulationsService,
        classroom,
        productId,
      },
      false
    ).then((response) => new Map(response.map((r) => [r.userId, r])))
  );
  useEffect(() => {
    if (classroomId && productId) {
      getMasteryInfo();
    }
  }, [classroomId, productId]);

  const nameSearch = useSearch({
    data: users,
    searchKeys: ["firstName", "lastName"],
  });

  const onResultSelected = (option) => {
    setUsers([option]);
  };

  const onNameTermChange = (term) => {
    setSearchActive(term.length > 0);
    setSearchTerm(term);
    const results = nameSearch.queryData(term);
    setUsers(results);
  };

  useEffect(() => {
    setSearchTerm("");
    setSearchActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const onTraineeAdd = async (trainees) => {
    const results = await Promise.allSettled(
      trainees.map((t) => classroomService.addTrainee(classroomId, t.id))
    );

    const successfulUserIds = results
      .map((result, idx) =>
        result.status === "fulfilled" ? trainees[idx].id : null
      )
      .filter(Boolean);
    if (successfulUserIds.length > 0) {
      await trackLearnersAssignedToClassroom(successfulUserIds, classroomId);
    }

    setOpenUserModal();
    addNotification({
      type: NotificationType.success,
      icon: ["fa-solid", "circle-check"],
      message: getSuccessMessage(trainees, classroom),
    });
    onUsersUpdate && onUsersUpdate();
    return Promise.resolve();
  };

  useEffect(() => {
    if (!loadingCareersExplored && !loadingMasteryInfo) {
      if (data && masteryInfo) {
        const usersData = data.map((t) => {
          const careersExploredData = careersExplored[t.userId] || {};
          return {
            ...t,
            ...masteryInfo.get(t.userId),
            isSelected: !!careersExploredData.selectedCareer,
            careerPlans: careersExploredData.careerPlans ?? [],
            careersViewed: careersExploredData.exploredCareers?.length ?? 0,
            budgetPlans: careersExploredData.budgetPlans ?? [],
            isBudgetCreated: careersExploredData.budgetPlans?.some(
              (bp) => bp.budgetCompletedAt
            ),
          };
        });
        setUsers(usersData);
        nameSearch.updateData(usersData);
      } else {
        setUsers(data);
      }
    }
  }, [data, masteryInfo, careersExplored]);

  const addTraineeButton = !readOnly && (
    <>
      <Button
        icon={["fa-light", "head-side-goggles"]}
        onClick={() => setOpenUserModal(true)}
        size="small"
      >
        {addUser}
      </Button>
      <AddUserModal
        open={openAddUserModal && !rosterModalOpened}
        orgCode={orgCode}
        onCancel={setOpenUserModal}
        onAddTrainee={onTraineeAdd}
        classroomId={classroomId}
        async
        userLabels={{ singular, plural }}
        classroomProduct={productId}
        allProducts={products}
        openRosterModal={openRosterModal}
        isProfessionalLearning={classroom.isProfessionalLearning}
      />
    </>
  );

  return (
    <div className="classroom-trainees-container">
      <div className="actions">
        <div className="left-actions">
          <SearchBox
            placeholder="Search First or Last Name"
            query={nameSearch.queryData}
            onSearchTermChange={onNameTermChange}
            onResultSelected={onResultSelected}
            // The label is a composite the name query can't match, so a pick
            // must not be re-run as a search term (it would blank the list).
            searchOnSelect={false}
            customOptionLabel={(r) => `${r?.lastName}, ${r?.firstName}`}
            searchTerm={searchTerm}
            disabled={!users || data?.length === 0}
          />
          {showStatusFilter && (
            <RecordStatusDropdown
              className="learner-status-dropdown"
              selected={statusFilter}
              onChange={onStatusFilterChange}
            />
          )}
        </div>
        {addTraineeButton}
      </div>
      <div className="trainees-table-wrapper">
        {loadingUsers || !users || loadingMasteryInfo ? (
          <Loader overlay />
        ) : (
          <ClassroomUsersMasteryTable
            responsiveStyles={responsiveStyles}
            data={users}
            readOnly={readOnly}
            classroom={classroom}
            getUsers={getUsers}
            showSuccessNotification={showSuccessNotification}
            productId={productId}
            statusFilter={statusFilter}
            zeroStateMessage={
              <ZeroStateMessage
                role={Role.STUDENT}
                containerLabel="classroom"
                filtered={searchActive}
                statusLabel={statusFilter}
                onShowButton={
                  !readOnly &&
                  !searchActive &&
                  statusFilter === RecordStatus.Active
                    ? () => setOpenUserModal(true)
                    : undefined
                }
              />
            }
          />
        )}
      </div>
    </div>
  );
};
